/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import {
  Button as ButtonCore,
  type ContextValue,
  DEFAULT_SLOT,
  Provider,
  Tag as TagCore,
  type TagProps as TagCoreProps,
  composeRenderProps,
  useContextProps,
} from 'react-aria-components';
import { CloseStrokeIcon } from '../../icons/close-stroke/close-stroke.icon';
import { $ } from '../../tokens.stylex';
import { BUTTON_SLOTS } from '../button/button.component';
import { IconContext, type IconProps } from '../icon/icon.component';
import { Text, TextContext, type TextProps } from '../text/text.component';

interface BadgeTagProps extends Omit<TagCoreProps, 'className' | 'style'> {
  /** The size of the badge. (Default: 20) */
  size?: keyof typeof sizeVariants;
  /** The variant of the badge. (Default: blueLight) */
  color?: keyof typeof fillColorVariants;
  /** Whether the badge is rounded. (Default: false) */
  isRounded?: boolean;
  /** Whether the badge is pastel. (Default: false) */
  isPastel?: boolean;
  style?: stylex.StyleXStyles;
}

const BadgeTagContext = createContext<ContextValue<BadgeTagProps, HTMLDivElement>>({});

const BadgeTag = forwardRef((props: BadgeTagProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, BadgeTagContext);

  const {
    size = 20,
    color = 'blueLight',
    isPastel = false,
    isRounded = false,
    style,
    children,
    ...otherProps
  } = props;

  return (
    <TagCore
      ref={ref}
      {...stylex.props(
        styles.base,
        isRounded && styles.rounded,
        sizeVariants[size],
        isPastel ? pastelColorVariants[color] : fillColorVariants[color],
        style
      )}
      {...otherProps}>
      {composeRenderProps(children, (children, renderProps) => (
        <Provider values={[
          [TextContext, {
            color: textColors[color],
            size: 12,
            slot: null!,
            style: textStyles.base,
          }],
          [IconContext, {
            slots: {
              [DEFAULT_SLOT]: {
                color: iconColors[color],
                size: 12,
              },
              [BUTTON_SLOTS.RIGHT]: {
                color: iconColors[color],
                size: 8,
                style: removeIconStyles.base,
              },
            },
          }],
        ]}>
          {typeof children === 'string' ? <Text>{children}</Text> : children}
          {renderProps.allowsRemoving && (
            <ButtonCore slot="remove" {...stylex.props(removeButtonStyles.base)}>
              <CloseStrokeIcon slot={BUTTON_SLOTS.RIGHT} />
            </ButtonCore>
          )}
        </Provider>
      ))}
    </TagCore>
  );
});

const textColors: Record<keyof typeof pastelColorVariants, TextProps['color']> = {
  blueLight: 'blueLight',
  blueDark: 'blueDark',
  disabled: 'disabled',
  green: 'positive',
  greyDark: 'secondary',
  greyLight: 'quaternary',
  orange: 'orange',
  red: 'critical',
  turquoise: 'turquoise',
  violet: 'violet',
  yellow: 'yellow',
};

const iconColors: Record<keyof typeof pastelColorVariants, IconProps['color']> = {
  blueLight: 'blueLight',
  blueDark: 'blueDark',
  disabled: 'disabled',
  green: 'positive',
  greyDark: 'secondary',
  greyLight: 'quaternary',
  orange: 'orange',
  red: 'critical',
  turquoise: 'turquoise',
  violet: 'violet',
  yellow: 'yellow',
};

const styles = stylex.create({
  base: {
    borderRadius: 4,
    borderStyle: 'none',
    gap: 4,
    overflow: 'hidden',
    textDecoration: 'none',
    alignItems: 'center',
    boxSizing: 'border-box',
    cursor: 'default',
    display: 'inline-flex',
    justifyContent: 'center',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    height: 20,
    maxWidth: '100%',
    paddingBottom: 4,
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 4,
    width: 'fit-content',
  },
  rounded: {
    borderRadius: 999,
  },
});

const fillColorVariants = stylex.create({
  red: { backgroundColor: $.surfaceCritical },
  yellow: { backgroundColor: $.accentSun60 },
  orange: { backgroundColor: $.accentPumpkin60 },
  green: { backgroundColor: $.surfacePositive },
  turquoise: { backgroundColor: $.accentTurquoise60 },
  blueLight: { backgroundColor: $.accentOcean60 },
  blueDark: { backgroundColor: $.accentSky60 },
  violet: { backgroundColor: $.accentPlum60 },
  greyDark: { backgroundColor: $.opacity48 },
  greyLight: { backgroundColor: $.opacity32 },
  disabled: { backgroundColor: $.buttonContentDisabled },
});

const pastelColorVariants = stylex.create({
  blueLight: { backgroundColor: $.accentOcean10 },
  blueDark: { backgroundColor: $.accentSky10 },
  disabled: { backgroundColor: $.buttonContentDisabled },
  green: { backgroundColor: $.surfacePositiveMedium },
  greyDark: { backgroundColor: $.opacity08 },
  greyLight: { backgroundColor: $.opacity06 },
  orange: { backgroundColor: $.accentPumpkin10 },
  red: { backgroundColor: $.surfaceCriticalSilent },
  turquoise: { backgroundColor: $.accentTurquoise10 },
  violet: { backgroundColor: $.accentPlum10 },
  yellow: { backgroundColor: $.accentSun10 },
});


const sizeVariants = stylex.create({
  16: {
    gap: 2,
    height: 16,
    paddingBottom: 3.5,
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 2.5,
  },
  20: {
    gap: 4,
    height: 20,
    paddingBottom: 4,
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 4,
  },
  32: {
    gap: 4,
    height: 32,
    paddingBottom: 8,
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 8,
  },
});

const textStyles = stylex.create({
  base: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
});

const removeButtonStyles = stylex.create({
  base: {
    padding: 0,
    borderStyle: 'none',
    alignItems: 'center',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'inline-flex',
    justifyContent: 'center',
  },
});

const removeIconStyles = stylex.create({
  base: {
    flexShrink: 0,
  },
});

export { BadgeTag, BadgeTagContext, type BadgeTagProps };
